import { z } from "zod";
/**
 * TASK-048 — Weekly PDF reports wire schemas.
 *
 * Shared by the backend Reports API + the Mini App Reports view. The
 * backend Report repo / job pipeline produces these shapes; the Mini App
 * useReportsQuery hook consumes them.
 *
 * SCOPE: pointers and metadata only. The PDF binary itself lives in
 * Supabase Storage (bucket "reports") behind a 7-day signed URL — no
 * per-message content ever surfaces here. The PDF document carries the
 * (text-free) top-post + heatmap visuals; this wire shape is purely an
 * index over the generated documents.
 */
/** Mirrors the Postgres ReportKind enum. */
export const reportKindSchema = z.enum(["WEEKLY", "AD_HOC"]);
/**
 * One Report row as the API surfaces it. `signedUrl` is the cached most-
 * recently-issued URL; the API refreshes it lazily when the row is read
 * within ~24h of `signedUrlExpiresAt`. Clients SHOULD NOT cache
 * `signedUrl` past `signedUrlExpiresAt` — re-fetch the listing to get a
 * refreshed URL.
 */
export const reportListItemSchema = z.object({
    id: z.string().min(1),
    communityId: z.string().min(1),
    communityTitle: z.string(),
    kind: reportKindSchema,
    /** ISO 8601 UTC. */
    weekStart: z.string().datetime({ offset: true }),
    /** ISO 8601 UTC. */
    weekEnd: z.string().datetime({ offset: true }),
    /** Pre-signed Supabase Storage URL (TTL ~7 days from issuance). */
    signedUrl: z.string().url(),
    /** ISO 8601 UTC — when the cached `signedUrl` expires. */
    signedUrlExpiresAt: z.string().datetime({ offset: true }),
    /** PDF size in bytes — surfaced in the UI. */
    fileSize: z.number().int().nonnegative(),
    /** ISO 8601 UTC — when the report was first generated. */
    generatedAt: z.string().datetime({ offset: true }),
});
/** GET /api/reports response. */
export const reportListResponseSchema = z.object({
    reports: z.array(reportListItemSchema),
});
/** POST /api/reports request — ad-hoc generation for one community. */
export const reportGenerateRequestSchema = z.object({
    communityId: z.string().min(1),
});
/**
 * POST /api/reports response. The job runs synchronously inside the
 * request lifetime (a single PDF render takes ~1-2s for the AC's
 * "<3s for a 1000-member community" budget); the response carries the
 * fresh Report row directly.
 */
export const reportGenerateResponseSchema = z.object({
    report: reportListItemSchema,
});
/**
 * 429 envelope for the per-user 3/day cap. Mirrors the rate-limit
 * envelope shape used elsewhere in the API for client-side retry-after
 * display.
 */
export const reportGenerateRateLimitedSchema = z.object({
    error: z.literal("RateLimited"),
    code: z.literal("report_quota_exhausted"),
    message: z.string(),
    /** Limit (3) + remaining slots in the current window. */
    limit: z.number().int().positive(),
    remaining: z.number().int().nonnegative(),
    /** Seconds until the quota resets. */
    retryAfterSeconds: z.number().int().nonnegative(),
});
/** Per-user ad-hoc generation budget per the AC ("3 ad-hoc/day per user"). */
export const REPORT_AD_HOC_DAILY_LIMIT = 3;
/** 24 hours in milliseconds — fixed window. */
export const REPORT_AD_HOC_WINDOW_MS = 24 * 60 * 60 * 1000;
//# sourceMappingURL=reports.js.map